import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, Mail } from "lucide-react";
import { z } from "zod";

export const Route = createFileRoute("/checkout/failed")({
  validateSearch: (search: Record<string, unknown>) =>
    z.object({ reason: z.string().optional() }).parse(search),
  head: () => ({
    meta: [
      { title: "Payment Failed — Recovery Trauma™" },
      {
        name: "description",
        content: "Your payment couldn't be completed. Please try again.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CheckoutFailedPage,
});

function CheckoutFailedPage() {
  // `reason` is intentionally ignored — buyers never see technical decline
  // codes. Stripe outcomes are recorded server-side in
  // payment_monitoring_events for internal admin review.
  return (
    <div className="min-h-[100svh] bg-gradient-to-b from-background via-background to-amber-100/30 px-4 py-16 sm:py-24">
      <div className="mx-auto flex max-w-[720px] items-center justify-center">
        <div className="w-full rounded-[2.25rem] border border-amber-400/50 bg-gradient-to-b from-amber-50/60 via-card/95 to-card/90 px-6 py-10 text-center shadow-[0_30px_80px_-30px_color-mix(in_oklab,oklch(0.75_0.15_75)_45%,transparent)] backdrop-blur sm:px-12 sm:py-14">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-900">
            <AlertTriangle className="h-8 w-8" strokeWidth={2} />
          </div>
          <h1 className="mt-5 font-display text-3xl leading-tight text-foreground sm:text-[38px]">
            Your payment wasn't completed
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">
            Your bank or payment provider did not approve this attempt. You
            have not been charged.
            <br />
            <br />
            Please wait a moment and try again, use a different payment
            method, or contact your bank if the problem continues.
          </p>
          <div className="mt-9 flex flex-col items-stretch gap-3 sm:items-center">
            <Link
              to="/membership"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-8 py-4 font-display text-[16px] font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5 sm:w-auto sm:px-10 sm:text-[17px]"
            >
              Try payment again
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/membership"
              className="rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Return to Membership
            </Link>
            <a
              href="mailto:support@recoverytrauma.com"
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <Mail className="h-4 w-4" /> Contact support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
